/**
 * TagManager ("ניהול טאגים") — route /tagManager.
 *
 * What it does
 *   - Tag list with counts (customers / subscribed / not subscribed), the email sequences each
 *     tag triggers, and its "auto-remove" tags. Create, edit (name, color, auto-remove), delete
 *     (only tags with no customers), merge tags.
 *   - A tag's customers (with their status): search, select, then move to another tag / change status / remove
 *     from the tag; add customers to the tag.
 *
 * Views (state `view`, same values as before the redesign):
 *   'list'    the tag table                          'merge'  merge dialog over the list
 *   'detail'  one tag's customers                    'add'    add-customers dialog over detail
 *                                                    'move'   move-to-tag dialog over detail
 *
 * API (unchanged):
 *   GET  /api/tags/stats                          tags + counts + sequences + excludesTags
 *   GET  /api/getCustomerStatuses                 statuses for "change status"
 *   GET  /api/tags/:id/customers?search=          a tag's customers
 *   GET  /api/tags/:id/customers/search-available?search=   customers NOT in the tag
 *   POST /api/tags  ·  PUT /api/tags/:id  ·  DELETE /api/tags/:id
 *   POST/DELETE /api/tags/:id/customers {customerIds}       add / remove
 *   POST /api/tags/move-customers {fromTagId,toTagId,customerIds}
 *   POST /api/tags/merge {sourceTagIds,targetTagId}
 *   POST /api/updateCustomerInfo {id,key:'status_id',val}   one call per selected customer
 *
 * Kept as-is on purpose: "manage customers" isn't available for a tag with 0 customers, and
 * delete is only offered for tags with 0 customers (the server enforces it too).
 * UI: built from the shared kit in src/ui (see client/UI_GUIDE.md).
 */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  PlusOutlined,
  MergeCellsOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  UserDeleteOutlined,
  UserAddOutlined,
  MailOutlined,
} from '@ant-design/icons';

import {
  Page,
  PageHeader,
  DataTable,
  RowActions,
  BulkActionBar,
  StatusBadge,
  EmptyState,
  Button,
  Input,
  Select,
  Modal,
  Tooltip,
  useBreakpoint,
  statusColor,
} from '../../ui';
import { showToast, confirmAction } from '../../ui/feedback';
import TagFormModal from './TagFormModal';
import MergeTagsModal from './MergeTagsModal';
import { DEFAULT_TAG_COLOR } from './tagColors';
import classes from './TagManager.css';

const TagManager = () => {
  // Data
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [customersInTag, setCustomersInTag] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // 'list', 'detail', 'add', 'move', 'merge'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchAvailable, setSearchAvailable] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [tagSort, setTagSort] = useState({ key: 'name', order: 'ascend' }); // UI only
  const [showCreate, setShowCreate] = useState(false); // the create form is a dialog now
  const [showStatus, setShowStatus] = useState(false); // "change status" dialog
  const [statusFilter, setStatusFilter] = useState([]); // UI only: filter the tag's customers by status ids ('none' = no status)
  const screens = useBreakpoint();

  // Form state
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(DEFAULT_TAG_COLOR);
  const [newTagExcludes, setNewTagExcludes] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [targetTag, setTargetTag] = useState(null);
  const [mergeSourceTags, setMergeSourceTags] = useState([]);
  const [mergeTargetTag, setMergeTargetTag] = useState(null);
  const [bulkStatus, setBulkStatus] = useState(null);

  useEffect(() => {
    loadTags();
    loadStatuses();
  }, []);

  // ---- Data loading ------------------------------------------------------------
  const loadStatuses = async () => {
    try {
      const response = await axios.get('/api/getCustomerStatuses');
      setStatuses(response.data || []);
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadTags = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/tags/stats');
      setTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomersInTag = async (tagId) => {
    setLoading(true);
    setSelectedCustomers([]); // Clear selected customers when switching tags
    try {
      const response = await axios.get(`/api/tags/${tagId}/customers?search=${searchTerm}`);
      setCustomersInTag(response.data.customers);
      setSelectedTag(response.data.tag);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchAvailableCustomers = async (tagId, search) => {
    try {
      const response = await axios.get(`/api/tags/${tagId}/customers/search-available?search=${search}`);
      setAvailableCustomers(response.data);
    } catch (error) {
      console.error('Error searching customers:', error);
    }
  };

  // ---- Tag CRUD -----------------------------------------------------------------
  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      showToast('נא להזין שם לטאג');
      return;
    }
    try {
      await axios.post('/api/tags', {
        name: newTagName.trim(),
        color: newTagColor,
        excludesTags: newTagExcludes,
      });
      setNewTagName('');
      setNewTagColor(DEFAULT_TAG_COLOR);
      setNewTagExcludes([]);
      setShowCreate(false);
      loadTags();
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;
    try {
      await axios.put(`/api/tags/${editingTag.id}`, {
        name: editingTag.name,
        color: editingTag.color,
        excludesTags: editingTag.excludesTags || [],
      });
      setEditingTag(null);
      loadTags();
      if (selectedTag && selectedTag.id === editingTag.id) {
        setSelectedTag({ ...selectedTag, ...editingTag });
      }
    } catch (error) {
      console.error('Error updating tag:', error);
    }
  };

  const handleDeleteTag = (tagId, tagName) => {
    confirmAction({
      text: `למחוק את הטאג "${tagName}"? (אפשרי רק כשאין בו לקוחות)`,
      onYes: async () => {
        try {
          await axios.delete(`/api/tags/${tagId}`);
          loadTags();
          if (selectedTag && selectedTag.id === tagId) {
            setSelectedTag(null);
            setView('list');
          }
        } catch (error) {
          console.error('Error deleting tag:', error);
        }
      },
    });
  };

  // ---- Customers in a tag -------------------------------------------------------
  const handleAddCustomers = async () => {
    if (selectedCustomers.length === 0) {
      showToast('נא לבחור לקוחות להוספה');
      return;
    }
    try {
      await axios.post(`/api/tags/${selectedTag.id}/customers`, { customerIds: selectedCustomers });
      setSelectedCustomers([]);
      setAvailableCustomers([]);
      setSearchAvailable('');
      loadCustomersInTag(selectedTag.id);
      loadTags();
      setView('detail');
    } catch (error) {
      console.error('Error adding customers:', error);
    }
  };

  const handleRemoveCustomers = () => {
    if (selectedCustomers.length === 0) {
      showToast('נא לבחור לקוחות להסרה');
      return;
    }
    confirmAction({
      text: `להסיר ${selectedCustomers.length} לקוח/ות מהטאג?`,
      onYes: async () => {
        try {
          await axios.delete(`/api/tags/${selectedTag.id}/customers`, { data: { customerIds: selectedCustomers } });
          setSelectedCustomers([]);
          loadCustomersInTag(selectedTag.id);
          loadTags();
        } catch (error) {
          console.error('Error removing customers:', error);
        }
      },
    });
  };

  const handleMoveCustomers = () => {
    if (!targetTag || selectedCustomers.length === 0) {
      showToast('נא לבחור טאג יעד ולקוחות להעברה');
      return;
    }
    confirmAction({
      text: `להעביר ${selectedCustomers.length} לקוח/ות לטאג "${targetTag.name}"?`,
      onYes: async () => {
        try {
          await axios.post('/api/tags/move-customers', {
            fromTagId: selectedTag.id,
            toTagId: targetTag.id,
            customerIds: selectedCustomers,
          });
          setSelectedCustomers([]);
          setTargetTag(null);
          loadCustomersInTag(selectedTag.id);
          loadTags();
          setView('detail');
        } catch (error) {
          console.error('Error moving customers:', error);
        }
      },
    });
  };

  const handleMergeTags = () => {
    if (mergeSourceTags.length === 0 || !mergeTargetTag) {
      showToast('נא לבחור טאגי מקור וטאג יעד');
      return;
    }
    if (mergeSourceTags.some((tag) => tag.id === mergeTargetTag.id)) {
      showToast('אי אפשר למזג טאג עם עצמו');
      return;
    }
    const sourceNames = mergeSourceTags.map((t) => t.name).join('", "');
    confirmAction({
      text: `למזג ${mergeSourceTags.length} טאג/ים לתוך "${mergeTargetTag.name}"? טאגי המקור ("${sourceNames}") יימחקו וכל הלקוחות שלהם יועברו. אי אפשר לבטל.`,
      onYes: async () => {
        try {
          await axios.post('/api/tags/merge', {
            sourceTagIds: mergeSourceTags.map((t) => t.id),
            targetTagId: mergeTargetTag.id,
          });
          setMergeSourceTags([]);
          setMergeTargetTag(null);
          loadTags();
          setView('list');
        } catch (error) {
          console.error('Error merging tags:', error);
        }
      },
    });
  };

  const handleBulkAssignStatus = () => {
    if (!bulkStatus) {
      showToast('אנא בחר סטטוס');
      return;
    }
    if (selectedCustomers.length === 0) {
      showToast('אנא בחר לקוחות להחלת הסטטוס');
      return;
    }
    let statusName = 'Unknown';
    try {
      statusName = statuses.find((s) => s.id === bulkStatus).name;
    } catch (error) {
      console.error('Error finding status name:', error);
    }
    confirmAction({
      text: `להחיל סטטוס "${statusName}" על ${selectedCustomers.length} לקוח/ות נבחרים?`,
      onYes: async () => {
        try {
          // Update status for each selected customer
          await Promise.all(
            selectedCustomers.map((customerId) =>
              axios.post(`/api/updateCustomerInfo`, { id: customerId, val: bulkStatus, key: 'status_id' })
            )
          );
          setBulkStatus(null);
          setSelectedCustomers([]);
          setShowStatus(false);
          loadCustomersInTag(selectedTag.id);
        } catch (error) {
          console.error('Error assigning status:', error);
        }
      },
    });
  };

  // Status filter (client-side, on the already-loaded list). Selection is trimmed to the rows
  // still visible, so bulk actions never touch customers hidden by the filter.
  const matchesStatus = (c) =>
    statusFilter.length === 0 || statusFilter.includes(c.status_id) || (statusFilter.includes('none') && !c.status_id);
  const visibleCustomers = customersInTag.filter(matchesStatus);

  function onStatusFilter(values) {
    setStatusFilter(values);
    const stillVisible = new Set(
      customersInTag
        .filter((c) => values.length === 0 || values.includes(c.status_id) || (values.includes('none') && !c.status_id))
        .map((c) => c.id)
    );
    setSelectedCustomers(selectedCustomers.filter((id) => stillVisible.has(id)));
  }

  const openTag = (tag) => {
    setStatusFilter([]);
    loadCustomersInTag(tag.id);
    setView('detail');
  };

  // ---- Render --------------------------------------------------------------------
  return (
    <Page>
      {view === 'list' || view === 'merge' ? renderTagList() : renderTagDetail()}

      {/* Create / edit tag */}
      <TagFormModal
        open={showCreate}
        title="טאג חדש"
        okText="צור טאג"
        value={{ name: newTagName, color: newTagColor, excludesTags: newTagExcludes }}
        onChange={(v) => {
          if (v.name !== undefined) setNewTagName(v.name);
          if (v.color !== undefined) setNewTagColor(v.color);
          if (v.excludesTags !== undefined) setNewTagExcludes(v.excludesTags);
        }}
        onOk={handleCreateTag}
        onCancel={() => setShowCreate(false)}
        allTags={tags}
        selfId={null}
      />
      <TagFormModal
        open={!!editingTag}
        title="עריכת טאג"
        okText="שמור"
        value={editingTag}
        onChange={(v) => setEditingTag({ ...editingTag, ...v })}
        onOk={handleUpdateTag}
        onCancel={() => setEditingTag(null)}
        allTags={tags}
        selfId={editingTag ? editingTag.id : null}
      />

      {/* Merge tags */}
      <MergeTagsModal
        open={view === 'merge'}
        tags={tags}
        sources={mergeSourceTags}
        target={mergeTargetTag}
        onSources={setMergeSourceTags}
        onTarget={setMergeTargetTag}
        onOk={handleMergeTags}
        onCancel={() => {
          setView('list');
          setMergeSourceTags([]);
          setMergeTargetTag(null);
        }}
      />

      {selectedTag ? renderAddModal() : null}
      {selectedTag ? renderMoveModal() : null}
      {selectedTag ? renderStatusModal() : null}
    </Page>
  );

  // Tag list ----------------------------------------------------------------------
  function renderTagList() {
    const filtered = tags.filter((tag) => tag.name.toLowerCase().includes(tagFilter.toLowerCase()));
    const dir = tagSort.order === 'ascend' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      const x = a[tagSort.key];
      const y = b[tagSort.key];
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x || '').localeCompare(String(y || ''), 'he') * dir;
    });
    const tagName = (id) => {
      const t = tags.find((x) => x.id === id);
      return t ? t.name : `ID: ${id}`;
    };

    return (
      <>
        <PageHeader
          title="ניהול טאגים"
          subtitle={`${tags.length} טאגים`}
          actions={
            <>
              <Button icon={<MergeCellsOutlined />} onClick={() => setView('merge')}>
                מזג טאגים
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>
                טאג חדש
              </Button>
            </>
          }
        />

        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined />}
          placeholder="חפש טאג לפי שם…"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          suffix={tagFilter ? <span className={classes.found}>נמצאו {filtered.length}</span> : null}
        />

        <DataTable
          data={sorted}
          loading={loading}
          sort={tagSort}
          onSort={(key) =>
            setTagSort((s) =>
              s.key === key
                ? { key, order: s.order === 'ascend' ? 'descend' : 'ascend' } // same column → flip
                : { key, order: key === 'name' ? 'ascend' : 'descend' } // new column: names A→Z, counts high→low
            )
          }
          fit
          onRowClick={(tag) => (tag.totalCustomers > 0 ? openTag(tag) : null)}
          empty={<EmptyState title={tagFilter ? 'לא נמצאו טאגים' : 'אין עדיין טאגים'} />}
          columns={[
            {
              key: 'name',
              title: 'טאג',
              sortable: true,
              render: (_, tag) => (
                <span className={classes.tagName}>
                  <span className={classes.dot} style={{ background: tag.color }} />
                  {tag.name}
                </span>
              ),
            },
            {
              key: 'totalCustomers',
      dataIndex: 'totalCustomers',
              title: 'לקוחות',
              sortable: true,
              width: 100,
              render: (v) => <span className="ui-tabular">{v}</span>,
            },
            {
              // "100 / 120" + a thin bar: customers in the tag whose email switch is on
              // (emailSubscribed) out of all its customers. Sorts by the subscribed count.
              key: 'subscribedCount',
              dataIndex: 'subscribedCount',
              title: 'מקבלים מיילים',
              sortable: true,
              width: 150,
              responsive: ['md'],
              render: (v, tag) => {
                const pct = tag.totalCustomers ? Math.round((v / tag.totalCustomers) * 100) : 0;
                return (
                  <Tooltip title={`${v} מקבלים מיילים · ${tag.unsubscribedCount} חסומים למיילים`} mouseEnterDelay={0.3}>
                    <div className={classes.ratio}>
                      <span className="ui-tabular">
                        <strong>{v}</strong>
                        <span className={classes.ratioOf}> / {tag.totalCustomers}</span>
                      </span>
                      <span className={classes.ratioBar}>
                        <span className={classes.ratioFill} style={{ width: `${pct}%` }} />
                      </span>
                    </div>
                  </Tooltip>
                );
              },
            },
            {
              key: 'sequences',
              title: 'רצפי אימייל',
              responsive: ['lg'],
              render: (_, tag) =>
                tag.emailSequences && tag.emailSequences.length > 0 ? (
                  <span className={classes.badges}>
                    {tag.emailSequences.map((seq, i) => (
                      <StatusBadge key={i} color={seq.isActive ? '#16A34A' : '#A1A1AA'}>
                        <MailOutlined /> {seq.name}
                        {seq.isActive ? '' : ' (מושהה)'}
                      </StatusBadge>
                    ))}
                  </span>
                ) : (
                  <span className={classes.muted}>—</span>
                ),
            },
            {
              key: 'excludes',
              title: 'הסרה אוטומטית',
              responsive: ['xl'],
              render: (_, tag) =>
                tag.excludesTags && tag.excludesTags.length > 0 ? (
                  <Tooltip title="כשטאג זה מתווסף ללקוח, הטאגים האלה מוסרים ממנו">
                    <span className={classes.excludes}>{tag.excludesTags.map(tagName).join(', ')}</span>
                  </Tooltip>
                ) : (
                  <span className={classes.muted}>—</span>
                ),
            },
            {
              key: 'actions',
              title: '',
              align: 'left',
              width: screens.md === false ? 64 : 120, // phones: a single "⋯" menu
              render: (_, tag) => (
                <RowActions
                  actions={[
                    {
                      key: 'manage',
                      icon: <TeamOutlined />,
                      label: 'ניהול לקוחות',
                      hidden: tag.totalCustomers === 0,
                      onClick: () => openTag(tag),
                    },
                    { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => setEditingTag({ ...tag }) },
                    {
                      key: 'delete',
                      icon: <DeleteOutlined />,
                      label: 'מחיקה',
                      danger: true,
                      hidden: tag.totalCustomers !== 0,
                      onClick: () => handleDeleteTag(tag.id, tag.name),
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </>
    );
  }

  // One tag's customers ---------------------------------------------------------
  function renderTagDetail() {
    if (!selectedTag) {
      return <DataTable data={[]} loading columns={[{ key: 'x', title: '' }]} />;
    }
    const backToList = () => {
      setView('list');
      setSelectedTag(null);
    };
    return (
      <>
        <PageHeader
          title={
            <span className={classes.detailTitle}>
              <Tooltip title="חזרה לטאגים">
                <Button type="text" icon={<ArrowRightOutlined />} onClick={backToList} aria-label="חזרה לטאגים" />
              </Tooltip>
              <span className={classes.dotLarge} style={{ background: selectedTag.color || DEFAULT_TAG_COLOR }} />
              {selectedTag.name}
            </span>
          }
          subtitle={
            statusFilter.length ? `${visibleCustomers.length} מתוך ${customersInTag.length} לקוחות` : `${customersInTag.length} לקוחות`
          }
          actions={
            <>
              <Button icon={<EditOutlined />} onClick={() => setEditingTag({ ...tags.find((t) => t.id === selectedTag.id), ...selectedTag })}>
                עריכת טאג
              </Button>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => {
                  setSelectedCustomers([]);
                  searchAvailableCustomers(selectedTag.id, '');
                  setView('add');
                }}
              >
                הוסף לקוחות
              </Button>
            </>
          }
        />

        <div className={classes.detailFilters}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="חפש לקוחות בטאג (Enter)…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={() => loadCustomersInTag(selectedTag.id)}
          />
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            className={classes.statusFilter}
            placeholder="כל הסטטוסים"
            value={statusFilter}
            onChange={onStatusFilter}
            options={[
              ...statuses.map((st) => ({ value: st.id, label: st.name })),
              { value: 'none', label: 'ללא סטטוס' },
            ]}
          />
        </div>

        <DataTable
          data={visibleCustomers}
          loading={loading}
          fit
          selection={{ selectedKeys: selectedCustomers, onChange: setSelectedCustomers }}
          empty={<EmptyState title="אין לקוחות בטאג זה" />}
          columns={withStatusColumn(customerColumns)}
        />

        <BulkActionBar
          count={selectedCustomers.length}
          total={visibleCustomers.length}
          onClear={() => setSelectedCustomers([])}
          actions={[
            {
              key: 'move',
              icon: <SwapOutlined />,
              label: 'העבר לטאג אחר',
              onClick: () => {
                setTargetTag(null);
                setView('move');
              },
            },
            { key: 'status', icon: <EditOutlined />, label: 'שינוי סטטוס', onClick: () => setShowStatus(true) },
            { key: 'remove', icon: <UserDeleteOutlined />, label: 'הסר מהטאג', onClick: handleRemoveCustomers },
          ]}
        />
      </>
    );
  }

  // The tag's customer list also shows each customer's status (status_id comes with the
  // customers; names from /api/getCustomerStatuses) — so "change status" isn't done blind.
  function withStatusColumn(columns) {
    const statusColumn = {
      key: 'status',
      title: 'סטטוס',
      dataIndex: 'status_id',
      width: 140,
      render: (id) => {
        const st = statuses.find((s) => s.id === id);
        return st ? <StatusBadge color={statusColor(id)}>{st.name}</StatusBadge> : <span className={classes.muted}>—</span>;
      },
    };
    return [columns[0], statusColumn, ...columns.slice(1)];
  }

  // Add customers to the tag -------------------------------------------------
  function renderAddModal() {
    const cancel = () => {
      setView('detail');
      setSelectedCustomers([]);
    };
    return (
      <Modal
        open={view === 'add'}
        title={`הוספת לקוחות ל"${selectedTag.name}"`}
        width={760}
        onCancel={cancel}
        footer={
          <>
            <Button onClick={cancel}>ביטול</Button>
            <Button type="primary" icon={<PlusOutlined />} disabled={selectedCustomers.length === 0} onClick={handleAddCustomers}>
              הוסף {selectedCustomers.length} לקוח/ות
            </Button>
          </>
        }
      >
        <div className={classes.modalBody}>
          <Input.Search
            allowClear
            enterButton="חפש"
            placeholder="חפש לפי שם, אימייל או טלפון…"
            value={searchAvailable}
            onChange={(e) => setSearchAvailable(e.target.value)}
            onSearch={(v) => searchAvailableCustomers(selectedTag.id, v)}
          />
          <DataTable
            data={availableCustomers}
            selection={{ selectedKeys: selectedCustomers, onChange: setSelectedCustomers }}
            onRowClick={(c) =>
              setSelectedCustomers(
                selectedCustomers.includes(c.id) ? selectedCustomers.filter((id) => id !== c.id) : [...selectedCustomers, c.id]
              )
            }
            pagination={{ pageSize: 25 }}
            empty={<EmptyState compact title={searchAvailable ? 'לא נמצאו לקוחות' : 'חפשו לקוחות להוספה'} />}
            columns={customerColumns}
          />
        </div>
      </Modal>
    );
  }

  // Move selected customers to another tag --------------------------------
  function renderMoveModal() {
    const cancel = () => {
      setView('detail');
      setTargetTag(null);
    };
    return (
      <Modal
        open={view === 'move'}
        title={`העברת ${selectedCustomers.length} לקוח/ות`}
        okText="העבר לקוחות"
        okButtonProps={{ disabled: !targetTag }}
        cancelText="ביטול"
        onOk={handleMoveCustomers}
        onCancel={cancel}
        destroyOnHidden
      >
        <div className={classes.modalBody}>
          <div className={classes.moveFrom}>
            מטאג <span className={classes.dot} style={{ background: selectedTag.color }} />
            <strong>{selectedTag.name}</strong>
          </div>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="בחר טאג יעד…"
            style={{ width: '100%' }}
            value={targetTag ? targetTag.id : undefined}
            onChange={(id) => setTargetTag(tags.find((t) => t.id === id) || null)}
            options={tags
              .filter((t) => t.id !== selectedTag.id)
              .map((t) => ({ value: t.id, label: t.name, color: t.color, count: t.totalCustomers }))}
            optionRender={(opt) => (
              <span className={classes.optionWithDot}>
                <span className={classes.dot} style={{ background: opt.data.color }} />
                <span className={classes.optionName}>{opt.label}</span>
                <span className={`${classes.optionCount} ui-tabular`}>{opt.data.count} לקוחות</span>
              </span>
            )}
          />
        </div>
      </Modal>
    );
  }

  // Change status of the selected customers ---------------------------------
  function renderStatusModal() {
    return (
      <Modal
        open={showStatus}
        title="שינוי סטטוס"
        okText={`החל על ${selectedCustomers.length}`}
        okButtonProps={{ disabled: !bulkStatus || selectedCustomers.length === 0 }}
        cancelText="ביטול"
        onOk={handleBulkAssignStatus}
        onCancel={() => setShowStatus(false)}
      >
        <div className={classes.modalBody}>
          <p className={classes.muted}>שינוי סטטוס עבור {selectedCustomers.length} לקוחות נבחרים</p>
          <Select
            style={{ width: '100%' }}
            placeholder="בחר סטטוס…"
            value={bulkStatus || undefined}
            onChange={(v) => setBulkStatus(v)}
            options={statuses.map((s) => ({ value: s.id, label: s.name }))}
          />
        </div>
      </Modal>
    );
  }
};

// Columns shared by "customers in tag" and "add customers"
const customerColumns = [
  {
    key: 'name',
    title: 'שם',
    render: (_, c) => <span className={classes.customerName}>{`${c.firstname || ''} ${c.lastname || ''}`.trim() || '—'}</span>,
  },
  { key: 'mail', title: 'אימייל', dataIndex: 'mail', responsive: ['md'], render: (v) => <span dir="ltr">{v}</span> },
  { key: 'phone', title: 'טלפון', dataIndex: 'phone', responsive: ['sm'], render: (v) => <span className="ui-tabular">{v}</span> },
  {
    key: 'emailSubscribed',
    dataIndex: 'emailSubscribed',
    title: 'מנוי',
    width: 110,
    render: (v) => <StatusBadge color={v ? '#16A34A' : '#DC2626'}>{v ? 'מנוי' : 'לא מנוי'}</StatusBadge>,
  },
];

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(TagManager);
